import { useMixpanel } from 'gatsby-plugin-mixpanel'
import Cookies from 'js-cookie'
import { sha256 } from 'js-sha256'
import { useCallback, useEffect } from 'react'

import useHandlebarsContext from '../hooks/useHandlebarsContext'
import useSiteData from '../hooks/useSiteData'
import useSiteMetadata from '../hooks/useSiteMetadata'
import useUser from '../hooks/useUser'
import { MediaQueries } from '../types/constants'
import {
  MixpanelDeviceTypes,
  MixpanelEventTypes,
  MixpanelPayload,
  MixpanelTracking,
  MixpanelTrackingObject,
  isContentfulJSONType,
  mixpanelCookie,
  mixpanelCookieOptions,
} from '../types/mixpanel'
import { isBrowser, isProduction } from './env'
import insertHandlebarsTemplate from './insertHandlebarsTemplate'
import { logErrorToConsole } from './logging'

const logError = (e: unknown) => {
  if (!isProduction) {
    logErrorToConsole('The Mixpanel JSON object has incorrect format', e)
  }
}

const getContentfulTrackingData = (
  trackingData?: MixpanelTrackingObject['trackingData']
): Record<string, unknown> => {
  if (isContentfulJSONType(trackingData)) {
    // Though Contentful doesn't save the malformed JSON, this try...catch is
    // added for safety
    try {
      return JSON.parse(trackingData?.internal.content)
    } catch (e) {
      logError(e)

      return {}
    }
  }

  return trackingData ?? {}
}

export const getDevice = () =>
  window.matchMedia(MediaQueries.TabletAndBelow).matches
    ? MixpanelDeviceTypes.Mobile
    : MixpanelDeviceTypes.Desktop

/**
 * This is a custom hook used for bundling the event payload.
 * @returns Mixpanel event's common payload
 */
export function useMixpanelCommonEventPayload() {
  const { partnerId, sessionID } = useUser()
  const mixpanel = useMixpanel()

  const getMixpanelCommonEventPayload = useCallback(
    (eventName: MixpanelEventTypes) => {
      if (!isProduction) {
        return {}
      }

      const payload: MixpanelPayload = {
        website_host: window.location.hostname,
        user_id: mixpanel.get_distinct_id(),
        device: getDevice(),
        session_id: sessionID,
      }

      if (eventName === MixpanelEventTypes.Click) {
        return payload
      }

      const url = new URL(window.location.href)
      const pt = url.searchParams.get('pt')

      if (pt) {
        payload.partner_key = pt
        payload.partner_id = partnerId
      }

      return payload
    },
    [mixpanel, partnerId, sessionID]
  )

  return getMixpanelCommonEventPayload
}

/**
 * This hook manages the user identity in Mixpanel.
 * It identifies and sets the user properties for every new lead.
 * It sets the `mpUserId` cookie with either the random ID for logged-out state
 * or the lead ID for the logged-in state which is used to keep track of the user ID
 * across the React and PHP pages.
 * @param brandName The name of the brand
 */
export function useMixpanelIdentityManagement(): void {
  const mixpanel = useMixpanel()
  const { leadId, email, partnerName, isLoading } = useUser()
  const { domainName, brandName } = useSiteMetadata()

  useEffect(() => {
    if (!isProduction || isLoading) {
      return
    }

    const distinctId = Cookies.get(mixpanelCookie)
    const hasAlreadyIdentified = leadId?.toString() === distinctId

    if (!!leadId && !hasAlreadyIdentified && !!email) {
      mixpanel.identify(leadId)
      mixpanel.people.set({
        user_id: leadId,
        $email: sha256(email),
        brand: brandName,
        partner: partnerName,
      })

      Cookies.set(mixpanelCookie, leadId.toString(), {
        ...mixpanelCookieOptions,
      })
    } else {
      // User landed on the react page
      if (!distinctId) {
        Cookies.set(mixpanelCookie, mixpanel.get_distinct_id(), {
          domain: `.${domainName}`,
          ...mixpanelCookieOptions,
        })
        // User came from a PHP page
      } else if (distinctId !== mixpanel.get_distinct_id()) {
        mixpanel.register({
          distinct_id: distinctId,
        })
      }
    }
  }, [brandName, email, isLoading, leadId, mixpanel, partnerName, domainName])
}

/**
 * This hook tracks the CTA/Nav clicks via Mixpanel.
 * @param ctaText The text of the CTA
 * @param ctaLink The link CTA will navigate to OR a custom action like resume upload
 * @returns void
 */
export function useMixpanelCTAClickTracking() {
  // TODO: We probably can safe remove this
  useMixpanelIdentityManagement()
  const mixpanel = useMixpanel()
  const mixpanelCommonEventPayload = useMixpanelCommonEventPayload()
  const handlebarsContext = useHandlebarsContext()

  const ctaClickTracker = useCallback(
    (
      ctaText: string | null | undefined,
      ctaLink: string,
      mixpanelTracking?: MixpanelTracking | null
    ) => {
      const eventName = insertHandlebarsTemplate(
        mixpanelTracking?.eventName,
        handlebarsContext
      )

      if (!isProduction || !isBrowser || !eventName) {
        return
      }

      if (
        // Track the event on all pages by default
        !mixpanelTracking?.trackOn ||
        // OR track it on the pages mentioned in Contentful's "Track On" field
        mixpanelTracking.trackOn.includes(window.location.pathname)
      ) {
        const eventPayload: ReturnType<typeof mixpanelCommonEventPayload> & {
          page_path: string
          cta_link: string
          cta_text?: string
        } = {
          ...mixpanelCommonEventPayload(MixpanelEventTypes.Click),
          page_path: window.location.pathname,
          cta_link: ctaLink,
        }

        if (ctaText) {
          eventPayload.cta_text = ctaText
        }

        mixpanel.track(
          eventName,
          {
            ...eventPayload,
            ...getContentfulTrackingData(mixpanelTracking?.trackingData),
          },
          {
            send_immediately: true,
          }
        )
      }
    },
    [mixpanel, mixpanelCommonEventPayload, handlebarsContext]
  )

  return ctaClickTracker
}

export const useMpEventType = () => {
  const { default_resume_product: defaultResumeProduct } = useSiteData()

  if (!isBrowser) {
    return null
  }

  const pathname = window.location.pathname

  if (pathname === '/') {
    return MixpanelEventTypes.HomePageEvent
  }

  if (pathname === `/${defaultResumeProduct.toLowerCase()}-review`) {
    return MixpanelEventTypes.FreeReviewPageEvent
  }

  if (pathname.startsWith('/new/')) {
    return MixpanelEventTypes.LandingPageEvent
  }

  return null
}

export const useMpEventTrackOn = (eventType: MixpanelEventTypes) => {
  const { default_resume_product: defaultResumeProduct } = useSiteData()

  switch (eventType) {
    case MixpanelEventTypes.CritiqueEvent:
      // @TODO [Mixpanel]: Clean the /new paths later once we have cut all the pages over
      return [
        `/new/${defaultResumeProduct.toLowerCase()}critiqueview`,
        `/${defaultResumeProduct.toLowerCase()}critiqueview`,
        `/new/${defaultResumeProduct.toLowerCase()}critiqueview-v2`,
        `/${defaultResumeProduct.toLowerCase()}critiqueview-v2`,
      ]
    case MixpanelEventTypes.JobSearchEvent:
      return [
        `/new/${defaultResumeProduct.toLowerCase()}-writing-total-job-search`,
      ]
    case MixpanelEventTypes.JobSearchEventLow:
      return [
        `/new/${defaultResumeProduct.toLowerCase()}-writing-total-job-search-2`,
      ]

    default:
      return null
  }
}
